// NameDialog — single-field dialog for explorers that name a row without
// picking a parent folder (the caller already knows the parent from the
// clicked folder). The caller supplies the copy and the submit handler; this
// component owns only the text input and the busy/error state around
// `onSubmit`. Use CreateItemDialog instead when the user should choose where
// the row lands.

import { useEffect, useState } from 'react'

import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { readMutationError } from './mutation-error'

const NAME_CONFLICT_FALLBACK = 'Something already exists at that path.'

export interface NameDialogProps {
  open: boolean
  title: string
  label: string
  placeholder?: string
  initialValue?: string
  submitLabel: string
  /** Shown under the input — e.g. where the new row will land. */
  hint?: string
  onClose: () => void
  /** Throws on failure (surfaced inline); resolves + closes on success. */
  onSubmit: (name: string) => Promise<void>
}

const NameDialog = ({
  open,
  title,
  label,
  placeholder,
  initialValue,
  submitLabel,
  hint,
  onClose,
  onSubmit
}: NameDialogProps) => {
  const [value, setValue] = useState(initialValue ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setValue(initialValue ?? '')
      setError(null)
      setSubmitting(false)
    }
  }, [open, initialValue])

  const handleSubmit = async (): Promise<void> => {
    const trimmed = value.trim()
    if (trimmed === '') {
      setError('Enter a name.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(trimmed)
      onClose()
    } catch (err) {
      setError(readMutationError(err, NAME_CONFLICT_FALLBACK))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next && !submitting ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-105">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="explorer-name-dialog-input">{label}</Label>
            <Input
              id="explorer-name-dialog-input"
              autoFocus
              value={value}
              placeholder={placeholder}
              disabled={submitting}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSubmit()
              }}
            />
            {hint ? <p className="m-0 text-11 text-ink-3">{hint}</p> : null}
          </div>
          {error ? (
            <div className="rounded-md bg-st-down-bg p-2.5 text-12 text-st-down" role="alert">
              {error}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting || value.trim() === ''}>
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default NameDialog
